import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@/contexts/UserContext';
import { useProfileCompletion } from '@/hooks/useProfileCompletion';
import { userService } from '@/services/userService';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export const ProfileCompletionForm: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, accountType, refreshUserData } = useUser();
  const { completionStatus, loading: completionLoading } = useProfileCompletion();
  const { toast } = useToast();
  
  const [formData, setFormData] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState<any>(null);

  // Redirect to role selection if no account type
  useEffect(() => {
    if (!accountType && !completionLoading) {
      navigate('/role-selection', { replace: true });
    }
  }, [accountType, completionLoading, navigate]);

  // Fetch current profile data
  useEffect(() => {
    const fetchProfileData = async () => {
      if (!currentUser?.user_id) return;
      
      try {
        const profile = await userService.getProfileByUserId(currentUser.user_id);
        if (profile) {
          setProfileData(profile);
          // Initialize form data with current profile data
          setFormData({
            display_name: profile.display_name || '',
            bio: profile.bio || '',
            location: profile.location || '',
            // Account-specific fields
            ...(accountType === 'talent' && profile.talent ? {
              name: profile.talent.name || '',
              sport: profile.talent.sport || '',
              achievements: profile.talent.achievements?.join(', ') || '',
            } : {}),
            ...(accountType === 'agent' && profile.agent ? {
              professional_name: profile.agent.professional_name || '',
              agency_name: profile.agent.agency_name || '',
              specialization: profile.agent.specialization?.join(', ') || '',
              professional_bio: profile.agent.professional_bio || '',
            } : {}),
            ...(accountType === 'brand' && profile.brand ? {
              company_name: profile.brand.company_name || '',
              industry: profile.brand.industry || '',
              description: profile.brand.description || '',
              marketing_objectives: profile.brand.marketing_objectives || '',
            } : {}),
            ...(accountType === 'venture' && profile.venture_capital ? {
              firm_name: profile.venture_capital.firm_name || '',
              investment_focus: profile.venture_capital.investment_focus?.join(', ') || '',
              stage_preference: profile.venture_capital.stage_preference?.join(', ') || '',
              investment_thesis: profile.venture_capital.investment_thesis || '',
            } : {}),
          });
        }
      } catch (error) {
        console.error('Error fetching profile data:', error);
      }
    };

    fetchProfileData();
  }, [currentUser?.user_id, accountType]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser?.user_id || !profileData) return;

    try {
      setLoading(true);

      // Update basic profile fields
      const profileUpdates: any = {};
      if (completionStatus.missingFields.includes('Display Name') && formData.display_name) {
        profileUpdates.display_name = formData.display_name;
      }
      if (completionStatus.missingFields.includes('Bio') && formData.bio) {
        profileUpdates.bio = formData.bio;
      }
      if (completionStatus.missingFields.includes('Location') && formData.location) {
        profileUpdates.location = formData.location;
      }

      if (Object.keys(profileUpdates).length > 0) {
        await userService.updateProfile(profileData.id, profileUpdates);
      }

      // Update account-specific data
      if (accountType === 'talent' && profileData.talent?.id) {
        const talentUpdates: any = {};
        if (completionStatus.missingFields.includes('Name') && formData.name) {
          talentUpdates.name = formData.name;
        }
        if (completionStatus.missingFields.includes('Sport') && formData.sport) {
          talentUpdates.sport = formData.sport;
        }
        if (completionStatus.missingFields.includes('Achievements') && formData.achievements) {
          talentUpdates.achievements = formData.achievements.split(',').map((a: string) => a.trim()).filter(Boolean);
        }

        if (Object.keys(talentUpdates).length > 0) {
          await supabase
            .from('talents')
            .update(talentUpdates)
            .eq('id', profileData.talent.id);
        }
      }

      if (accountType === 'agent' && profileData.agent?.id) {
        const agentUpdates: any = {};
        if (completionStatus.missingFields.includes('Professional Name') && formData.professional_name) {
          agentUpdates.professional_name = formData.professional_name;
        }
        if (completionStatus.missingFields.includes('Agency Name') && formData.agency_name) {
          agentUpdates.agency_name = formData.agency_name;
        }
        if (completionStatus.missingFields.includes('Specialization') && formData.specialization) {
          agentUpdates.specialization = formData.specialization.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
        if (completionStatus.missingFields.includes('Professional Bio') && formData.professional_bio) {
          agentUpdates.professional_bio = formData.professional_bio;
        }

        if (Object.keys(agentUpdates).length > 0) {
          await supabase
            .from('agents')
            .update(agentUpdates)
            .eq('id', profileData.agent.id);
        }
      }

      if (accountType === 'brand' && profileData.brand?.id) {
        const brandUpdates: any = {};
        if (completionStatus.missingFields.includes('Company Name') && formData.company_name) {
          brandUpdates.company_name = formData.company_name;
        }
        if (completionStatus.missingFields.includes('Industry') && formData.industry) {
          brandUpdates.industry = formData.industry;
        }
        if (completionStatus.missingFields.includes('Description') && formData.description) {
          brandUpdates.description = formData.description;
        }
        if (completionStatus.missingFields.includes('Marketing Objectives') && formData.marketing_objectives) {
          brandUpdates.marketing_objectives = formData.marketing_objectives;
        }

        if (Object.keys(brandUpdates).length > 0) {
          await supabase
            .from('brands')
            .update(brandUpdates)
            .eq('id', profileData.brand.id);
        }
      }

      if (accountType === 'venture' && profileData.venture_capital?.id) {
        const vcUpdates: any = {};
        if (completionStatus.missingFields.includes('Firm Name') && formData.firm_name) {
          vcUpdates.firm_name = formData.firm_name;
        }
        if (completionStatus.missingFields.includes('Investment Focus') && formData.investment_focus) {
          vcUpdates.investment_focus = formData.investment_focus.split(',').map((f: string) => f.trim()).filter(Boolean);
        }
        if (completionStatus.missingFields.includes('Stage Preference') && formData.stage_preference) {
          vcUpdates.stage_preference = formData.stage_preference.split(',').map((s: string) => s.trim()).filter(Boolean);
        }
        if (completionStatus.missingFields.includes('Investment Thesis') && formData.investment_thesis) {
          vcUpdates.investment_thesis = formData.investment_thesis;
        }

        if (Object.keys(vcUpdates).length > 0) {
          await supabase
            .from('venture_capitals')
            .update(vcUpdates)
            .eq('id', profileData.venture_capital.id);
        }
      }

      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully completed!",
      });

      await refreshUserData();
      navigate('/profile');

    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (completionLoading || !profileData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading profile...</p>
        </div>
      </div>
    );
  }

  const renderMissingFields = () => {
    const missingFields = completionStatus.missingFields.filter(field => field !== 'Profile not found' && field !== 'Error loading profile');
    
    if (missingFields.length === 0) {
      return (
        <div className="text-center py-12">
          <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-2">Profile Complete!</h2>
          <p className="text-muted-foreground mb-6">Your profile is fully completed. Great job!</p>
          <Button onClick={() => navigate('/profile')}>
            Return to Profile
          </Button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Basic Profile Fields */}
        {missingFields.includes('Display Name') && (
          <div className="space-y-2">
            <Label htmlFor="display_name">Display Name</Label>
            <Input
              id="display_name"
              value={formData.display_name || ''}
              onChange={(e) => handleInputChange('display_name', e.target.value)}
              placeholder="Enter your display name"
            />
          </div>
        )}

        {missingFields.includes('Bio') && (
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={formData.bio || ''}
              onChange={(e) => handleInputChange('bio', e.target.value)}
              placeholder="Tell us about yourself"
              rows={3}
            />
          </div>
        )}

        {missingFields.includes('Location') && (
          <div className="space-y-2">
            <Label htmlFor="location">Location</Label>
            <Input
              id="location"
              value={formData.location || ''}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="Enter your location"
            />
          </div>
        )}

        {/* Talent Fields */}
        {accountType === 'talent' && (
          <>
            {missingFields.includes('Name') && (
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>
            )}

            {missingFields.includes('Sport') && (
              <div className="space-y-2">
                <Label htmlFor="sport">Sport</Label>
                <Input
                  id="sport"
                  value={formData.sport || ''}
                  onChange={(e) => handleInputChange('sport', e.target.value)}
                  placeholder="Enter your sport"
                />
              </div>
            )}

            {missingFields.includes('Achievements') && (
              <div className="space-y-2">
                <Label htmlFor="achievements">Achievements</Label>
                <Textarea
                  id="achievements"
                  value={formData.achievements || ''}
                  onChange={(e) => handleInputChange('achievements', e.target.value)}
                  placeholder="Enter your achievements (comma separated)"
                  rows={3}
                />
              </div>
            )}
          </>
        )}

        {/* Agent Fields */}
        {accountType === 'agent' && (
          <>
            {missingFields.includes('Professional Name') && (
              <div className="space-y-2">
                <Label htmlFor="professional_name">Professional Name</Label>
                <Input
                  id="professional_name"
                  value={formData.professional_name || ''}
                  onChange={(e) => handleInputChange('professional_name', e.target.value)}
                  placeholder="Enter your professional name"
                />
              </div>
            )}

            {missingFields.includes('Agency Name') && (
              <div className="space-y-2">
                <Label htmlFor="agency_name">Agency Name</Label>
                <Input
                  id="agency_name"
                  value={formData.agency_name || ''}
                  onChange={(e) => handleInputChange('agency_name', e.target.value)}
                  placeholder="Enter your agency name"
                />
              </div>
            )}

            {missingFields.includes('Specialization') && (
              <div className="space-y-2">
                <Label htmlFor="specialization">Specialization</Label>
                <Input
                  id="specialization"
                  value={formData.specialization || ''}
                  onChange={(e) => handleInputChange('specialization', e.target.value)}
                  placeholder="Enter your specializations (comma separated)"
                />
              </div>
            )}

            {missingFields.includes('Professional Bio') && (
              <div className="space-y-2">
                <Label htmlFor="professional_bio">Professional Bio</Label>
                <Textarea
                  id="professional_bio"
                  value={formData.professional_bio || ''}
                  onChange={(e) => handleInputChange('professional_bio', e.target.value)}
                  placeholder="Enter your professional bio"
                  rows={3}
                />
              </div>
            )}
          </>
        )}

        {/* Brand Fields */}
        {accountType === 'brand' && (
          <>
            {missingFields.includes('Company Name') && (
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name || ''}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Enter your company name"
                />
              </div>
            )}

            {missingFields.includes('Industry') && (
              <div className="space-y-2">
                <Label htmlFor="industry">Industry</Label>
                <Input
                  id="industry"
                  value={formData.industry || ''}
                  onChange={(e) => handleInputChange('industry', e.target.value)}
                  placeholder="Enter your industry"
                />
              </div>
            )}

            {missingFields.includes('Description') && (
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Enter company description"
                  rows={3}
                />
              </div>
            )}

            {missingFields.includes('Marketing Objectives') && (
              <div className="space-y-2">
                <Label htmlFor="marketing_objectives">Marketing Objectives</Label>
                <Textarea
                  id="marketing_objectives"
                  value={formData.marketing_objectives || ''}
                  onChange={(e) => handleInputChange('marketing_objectives', e.target.value)}
                  placeholder="Enter your marketing objectives"
                  rows={3}
                />
              </div>
            )}
          </>
        )}

        {/* Venture Fields */}
        {accountType === 'venture' && (
          <>
            {missingFields.includes('Firm Name') && (
              <div className="space-y-2">
                <Label htmlFor="firm_name">Firm Name</Label>
                <Input
                  id="firm_name"
                  value={formData.firm_name || ''}
                  onChange={(e) => handleInputChange('firm_name', e.target.value)}
                  placeholder="Enter your firm name"
                />
              </div>
            )}

            {missingFields.includes('Investment Focus') && (
              <div className="space-y-2">
                <Label htmlFor="investment_focus">Investment Focus</Label>
                <Input
                  id="investment_focus"
                  value={formData.investment_focus || ''}
                  onChange={(e) => handleInputChange('investment_focus', e.target.value)}
                  placeholder="Enter investment focus areas (comma separated)"
                />
              </div>
            )}

            {missingFields.includes('Stage Preference') && (
              <div className="space-y-2">
                <Label htmlFor="stage_preference">Stage Preference</Label>
                <Input
                  id="stage_preference"
                  value={formData.stage_preference || ''}
                  onChange={(e) => handleInputChange('stage_preference', e.target.value)}
                  placeholder="Enter stage preferences (comma separated)"
                />
              </div>
            )}

            {missingFields.includes('Investment Thesis') && (
              <div className="space-y-2">
                <Label htmlFor="investment_thesis">Investment Thesis</Label>
                <Textarea
                  id="investment_thesis"
                  value={formData.investment_thesis || ''}
                  onChange={(e) => handleInputChange('investment_thesis', e.target.value)}
                  placeholder="Enter your investment thesis"
                  rows={3}
                />
              </div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/profile')}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Return to Profile
          </Button>
          
          <h1 className="text-3xl font-bold mb-2">Complete Your Profile</h1>
          <p className="text-muted-foreground mb-4">
            Fill out the missing information to complete your profile
          </p>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Profile completion</span>
              <span>{completionStatus.percentage}%</span>
            </div>
            <Progress value={completionStatus.percentage} className="h-2" />
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Missing Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              {renderMissingFields()}
              
              {completionStatus.missingFields.filter(field => 
                field !== 'Profile not found' && field !== 'Error loading profile'
              ).length > 0 && (
                <div className="flex gap-4 pt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => navigate('/profile')}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={loading}
                    className="flex-1"
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};